<template>
  <v-popover
    ref="filterPill"
    interactive
    tag="div"
    content-tag="div"
    trigger="click"
    placement="bottom-start"
    :role="'poper'"
    :on-show="onShow"
    :on-hide="onHide"
  >
    <div
      class="flex border border-solid rounded-2xl pl-1 min-w-0 pt"
      :class="{
        'pr-1': !dropdownOptions.length,
        'border-gray-500': !selectedValuesCount,
        'bg-highlight-bg': selectedValuesCount > 0,
        'border-blue-500': selectedValuesCount > 0,
      }"
      @click="pillClick"
    >
      <PillLabel
        v-if="!selectedValuesCount || singleValue"
        ref="filterPillLabel"
        :icon-name="icon"
        :icon-class="'w-3 h-3'"
        :class="['shrink-0', { 'pr-0': !variationsEnabled.length }, { 'pr-1': singleValue }]"
        :label="pillLabel"
      />
      <PillVariation
        v-if="variations.length"
        :variations="variationsEnabled"
        :preselected-variation="preselectedVariation"
        class="shrink-0 min-w-0"
        @set-variation="setVariation"
        @variation-dropdown-opened="onVariationDropdownShown"
      />
      <div
        v-if="!singleValue"
        ref="filterDropdown"
        class="flex py-0 px-1 min-w-0"
      >
        <FilterIcon
          v-for="(item, index) in firstThreeSelectedValues"
          :key="item.id"
          :icon-name="item?.customIcon"
          :icon-type="item?.iconType"
          :color="item?.customIconColor"
          :class="{
            'mr-1': index === firstThreeSelectedValues.length - 1,
          }"
          class="flex items-center justify-center px-0 mr"
          :icon-class="'text-gray-500'"
        />
        <PillCountLabel
          v-if="selectedValuesCount > 0"
          :count-label="countLabel"
          :values-count="selectedValuesCount"
        />
        <FilterIcon
          :icon-name="'chevron-down'"
          :class="{
            'pl-0': selectedValuesCount === 0,
          }"
          class="flex items-center justify-center px-1"
          :icon-class="'bg-icon-default'"
        />
      </div>
    </div>
    <template #content>
      <FilterDropdown
        ref="filterDropdown"
        :placeholder="dropdownPlaceholder"
        :hide-filter-popup="escHandler"
        :search-results="filteredDropdownOptions"
        :on-item-click="itemClicked"
        :on-item-right-side-click="itemRightSideClick"
        :select-only-search-result="checkItem"
        :show-search-input="showSearchInput"
        @query-change="dropdownQueryChange"
        @esc="escHandler"
      />
    </template>
  </v-popover>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import PillLabel from '@/components/widgets/filters/PillLabel.vue';
import PillVariation from '@/components/widgets/filters/PillVariation.vue';
import PillCountLabel from '@/components/widgets/filters/PillCountLabel.vue';
import FilterIcon from '@/components/widgets/filters/FilterIcon.vue';
import FilterDropdown from '@/components/widgets/filters/FilterDropdown.vue';
import useTranslate from '../../utilities/composables/useTranslate';

const emit = defineEmits(['value-change', 'variation-change', 'dropdown-closed']);

interface Variation {
  id: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    label: string;
    pluralLabel?: string;
    icon: string;
    preselectedVariation?: string;
    dropdownOptions?: Array<any>;
    variations?: Array<{ id: string; label: string }> | [];
    showSearchInput?: boolean;
    dropdownPlaceholder?: string;
    emptyMeansAll?: boolean;
    singleValue?: boolean;
    selectedValue?: boolean;
  }>(),
  {
    showSearchInput: true,
    dropdownOptions: () => [],
    dropdownPlaceholder: 'Search',
    variations: () => [],
    preselectedVariation: '',
    emptyMeansAll: false,
    pluralLabel: '',
    singleValue: false,
    selectedValue: false,
  }
);

const { translate } = useTranslate();

const searchQuery = ref('');

const filteredDropdownOptions = computed(() => {
  return props.dropdownOptions.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});
const filterPill = ref<any>(null);
const filterDropdown = ref<any>(null);
const filterPillLabel = ref(null);

const variationsEnabled = computed(() => {
  if (!props.variations.length) {
    return [];
  }
  // exclude should be disabled when everything is selected
  return (props.variations as Variation[])?.map?.((v: Variation) => {
    return {
      ...v,
      disabled: everythingSelected.value && v.id.includes('exclude'),
    };
  });
});

const selectedValuesCount = computed(() => {
  if (props.singleValue) {
    return props.selectedValue ? 1 : 0;
  }
  return props.dropdownOptions.filter((option) => option.checked).length;
});

const pillLabel = computed(() => {
  if (selectedValuesCount.value > 0 && !props.singleValue) {
    return `${props.label}:`;
  }
  return props.label;
});

const singleSelectedValue = computed(() => {
  if (selectedValuesCount.value !== 1) {
    return null;
  }
  return props.dropdownOptions.find((option) => option.checked);
});

const everythingSelected = computed(() => {
  return (
    selectedValuesCount.value === props.dropdownOptions.length ||
    (selectedValuesCount.value === 0 && props.emptyMeansAll)
  );
});

const firstThreeSelectedValues = computed(() => {
  return props.dropdownOptions.filter((option) => option.checked).slice(0, 3);
});

const countLabel = computed(() => {
  if (selectedValuesCount.value === 0 && !props.emptyMeansAll) {
    return translate('none');
  }
  if (selectedValuesCount.value === 1) {
    return singleSelectedValue.value?.label;
  }
  if (everythingSelected.value) {
    return translate('all');
  }
  return `${selectedValuesCount.value} ${props.pluralLabel.toLowerCase()}`;
});

const emitValueChange = (value: any) => {
  emit('value-change', value);
};

const itemClicked = (item: any) => {
  emitValueChange(item);
};

const setVariation = (variation: string) => {
  emit('variation-change', variation);
};

const hideFilterPillInstance = () => {
  filterPill.value?.hide?.();
};

const itemRightSideClick = (item: any) => {
  hideFilterPillInstance();
  emitValueChange(item);
  emit('dropdown-closed');
};

const checkItem = (item: any) => {
  emitValueChange(item);
};

const onVariationDropdownShown = () => {
  hideFilterPillInstance();
};

const escHandler = () => {
  hideFilterPillInstance();
};

const dropdownQueryChange = ({ value }: { value: string }) => {
  searchQuery.value = value;
};

const onShow = () => {
  if (props.singleValue) return false;
  filterDropdown.value?.focusInput?.();
};

const onHide = () => {
  searchQuery.value = '';
  emit('dropdown-closed');
};

const pillClick = () => {
  if (props.singleValue) {
    emitValueChange(!props.selectedValue);
    emit('dropdown-closed');
  }
};
</script>
